import { pages, subPackages, tabBar } from '@/pages.json'
import { isMpWeixin } from './platform'
import i18n from '@/locale/index'
import storage from '@/local/storage'
import { getStorage } from '@/utils/storage'
const { t } = i18n.global

const getLastPage = () => {
  // getCurrentPages() 至少有1个元素，所以不再额外判断
  // const lastPage = getCurrentPages().at(-1)
  // 上面那个在低版本安卓中打包回报错，所以改用下面这个【虽然我加了src/interceptions/prototype.ts，但依然报错】
  const pages = getCurrentPages()
  return pages[pages.length - 1]
}

/** 判断当前页面是否是tabbar页  */
export const getIsTabbar = () => {
  if (!tabBar) {
    return false
  }
  if (!tabBar.list.length) {
    // 通常有tabBar的话，list不能有空，且至少有2个元素，这里其实不用处理
    return false
  }
  const lastPage = getLastPage()
  const currPath = lastPage.route
  return !!tabBar.list.find((e) => e.pagePath === currPath)
}

/**
 * 获取当前页面路由的 path 路径和 redirectPath 路径
 * path 如 'pages/login/index'
 * redirectPath 如 'pages/demo/base/route-interceptor'
 */
export const currRoute = () => {
  const lastPage = getLastPage()
  const currRoute = (lastPage as any).$page
  // console.log('lastPage.$page:', currRoute)
  // console.log('lastPage.$page.fullpath:', currRoute.fullPath)
  // console.log('lastPage.$page.options:', currRoute.options)
  // console.log('lastPage.options:', (lastPage as any).options)
  // 经过多端测试，只有 fullPath 靠谱，其他都不靠谱
  const { fullPath } = currRoute as { fullPath: string }
  // console.log(fullPath)
  // eg: /pages/login/index?redirect=%2Fpages%2Fdemo%2Fbase%2Froute-interceptor (小程序)
  // eg: /pages/login/index?redirect=%2Fpages%2Froute-interceptor%2Findex%3Fname%3Dfeige%26age%3D30(h5)
  return getUrlObj(fullPath)
}

const ensureDecodeURIComponent = (url: string) => {
  if (url.startsWith('%')) {
    return ensureDecodeURIComponent(decodeURIComponent(url))
  }
  return url
}
/**
 * 解析 url 得到 path 和 query
 * 比如输入url: /pages/login/index?redirect=%2Fpages%2Fdemo%2Fbase%2Froute-interceptor
 * 输出: {path: /pages/login/index, query: {redirect: /pages/demo/base/route-interceptor}}
 */
export const getUrlObj = (url: string) => {
  const [path, queryStr] = url.split('?')
  // console.log(path, queryStr)

  if (!queryStr) {
    return {
      path,
      query: {},
    }
  }
  const query: Record<string, string> = {}
  queryStr.split('&').forEach((item) => {
    const [key, value] = item.split('=')
    // console.log(key, value)
    query[key] = ensureDecodeURIComponent(value) // 这里需要统一 decodeURIComponent 一下，可以兼容h5和微信y
  })
  return { path, query }
}
/**
 * 得到所有的需要登录的pages，包括主包和分包的
 * 这里设计得通用一点，可以传递key作为判断依据，默认是 needLogin, 与 route-block 配对使用
 * 如果没有传 key，则表示所有的pages，如果传递了 key, 则表示通过 key 过滤
 */
export const getAllPages = (key = 'needLogin') => {
  // 这里处理主包
  const mainPages = [
    ...pages
      .filter((page) => !key || page[key])
      .map((page) => ({
        ...page,
        path: `/${page.path}`,
      })),
  ]
  // 这里处理分包
  const subPages: any[] = []
  subPackages.forEach((subPageObj) => {
    // console.log(subPageObj)
    const { root } = subPageObj

    subPageObj.pages
      .filter((page) => !key || page[key])
      .forEach((page: { path: string } & Record<string, any>) => {
        subPages.push({
          ...page,
          path: `/${root}/${page.path}`,
        })
      })
  })
  const result = [...mainPages, ...subPages]
  // console.log(`getAllPages by ${key} result: `, result)
  return result
}

/**
 * 得到所有的需要登录的pages，包括主包和分包的
 * 只得到 path 数组
 */
export const getNeedLoginPages = (): string[] => getAllPages('needLogin').map((page) => page.path)

/**
 * 得到所有的需要登录的pages，包括主包和分包的
 * 只得到 path 数组
 */
export const needLoginPages: string[] = getAllPages('needLogin').map((page) => page.path)

/**
 * 根据微信小程序当前环境，判断应该获取的BaseUrl
 */
export const getEnvBaseUrl = () => {
  // 请求基准地址
  let baseUrl = import.meta.env.VITE_SERVER_BASEURL

  // 微信小程序端环境区分
  if (isMpWeixin) {
    const {
      miniProgram: { envVersion },
    } = uni.getAccountInfoSync()

    switch (envVersion) {
      case 'develop':
        baseUrl = import.meta.env.VITE_SERVER_BASEURL__WEIXIN_DEVELOP || baseUrl
        break
      case 'trial':
        baseUrl = import.meta.env.VITE_SERVER_BASEURL__WEIXIN_TRIAL || baseUrl
        break
      case 'release':
        baseUrl = import.meta.env.VITE_SERVER_BASEURL__WEIXIN_RELEASE || baseUrl
        break
    }
  }

  return baseUrl
}

/**
 * 根据微信小程序当前环境，判断应该获取的UPLOAD_BASEURL
 */
export const getEnvBaseUploadUrl = () => {
  // 请求基准地址
  let baseUploadUrl = import.meta.env.VITE_UPLOAD_BASEURL

  // 微信小程序端环境区分
  if (isMpWeixin) {
    const {
      miniProgram: { envVersion },
    } = uni.getAccountInfoSync()

    switch (envVersion) {
      case 'develop':
        baseUploadUrl = import.meta.env.VITE_UPLOAD_BASEURL__WEIXIN_DEVELOP || baseUploadUrl
        break
      case 'trial':
        baseUploadUrl = import.meta.env.VITE_UPLOAD_BASEURL__WEIXIN_TRIAL || baseUploadUrl
        break
      case 'release':
        baseUploadUrl = import.meta.env.VITE_UPLOAD_BASEURL__WEIXIN_RELEASE || baseUploadUrl
        break
    }
  }

  return baseUploadUrl
}

export function mergingStep<T = any>(wrapped: (...rest: any) => Promise<any>) {
  let runningInstance: Promise<T> | null = null
  return function (...args: any[]) {
    if (runningInstance) {
      // 若步骤正在进行，则监听并使用其执行结果，而不是重新发起该步骤
      return runningInstance
    }
    const res = wrapped.apply(this, args)

    if (!(res instanceof Promise)) {
      return res
    }
    runningInstance = res
    runningInstance
      .then(function () {
        runningInstance = null
      })
      .catch(function () {
        runningInstance = null
      })
    return runningInstance
  }
}

// Promise 版modal
export const showModal = (
  content: string,
  { showCancel, title, ...options }: UniApp.ShowModalOptions = {},
) => {
  return new Promise((resolve, reject) =>
    uni.showModal({
      showCancel: showCancel === true,
      title: title || t('modal.title'),
      confirmText: t('modal.confirm'),
      cancelText: t('modal.cancel'),
      ...options,
      content,
      success(e) {
        if (e.confirm) {
          return resolve(e)
        }
        reject(e)
      },
      fail: reject,
    }),
  )
}

// 金额保留两位小数
export const formatAmount = (amount: number) => {
  // 如果 amount 为 0，则返回 0
  if (!amount) {
    return '0.00'
  }
  return amount.toFixed(2)
}

// 复制文本
export const copyText = (text: string | number) => {
  uni.setClipboardData({
    data: text.toString(),
  })
}

export function blobtoFile(blob, fileName = '图片1', type = 'jpg') {
  return new File([blob], fileName, { type })
}

// 压缩前将file转换成img对象
export function readImg(file) {
  return new Promise((resolve, reject) => {
    // 确保 file 是一个 Blob 或 File 对象
    if (file instanceof Blob || file instanceof File) {
      const img = new Image()
      const reader = new FileReader()
      reader.onload = function (e) {
        img.src = e.target.result
      }
      reader.onerror = function (e) {
        reject(e)
      }
      reader.readAsDataURL(file)
      img.onload = function () {
        resolve(img)
      }
      img.onerror = function (e) {
        reject(e)
      }
    } else {
      reject(new Error('Input must be a Blob or File object'))
    }
  })
}

/**
 * 压缩图片
 *@param img 被压缩的img对象
 * @param type 压缩后转换的文件类型
 * @param mx 触发压缩的图片最大宽度限制
 * @param mh 触发压缩的图片最大高度限制
 */
export function compressImg(img, type, mx, mh) {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')
    const { width: originWidth, height: originHeight } = img
    // 最大尺寸限制
    const maxWidth = mx
    const maxHeight = mh
    // 目标尺寸
    let targetWidth = originWidth
    let targetHeight = originHeight
    if (originWidth > maxWidth || originHeight > maxHeight) {
      if (originWidth / originHeight > 1) {
        //  宽图片
        targetWidth = maxWidth
        targetHeight = Math.round(maxWidth * (originHeight / originWidth))
      } else {
        // 高图片
        targetHeight = maxHeight
        targetWidth = Math.round(maxHeight * (originWidth / originHeight))
      }
    }
    canvas.width = targetWidth
    canvas.height = targetHeight
    context.clearRect(0, 0, targetWidth, targetHeight)
    // 图片绘制
    context.drawImage(img, 0, 0, targetWidth, targetHeight)
    canvas.toBlob(function (blob) {
      resolve(blob)
    }, type || 'image/png')
  })
}

// 处理富文本媒体元素
export function handleRichTextMedia(html: string, type: 'img' | 'all' = 'all') {
  let result = html

  // 处理图片
  result = result.replace(/<img/gi, '<img class="richImg" style="max-width: 100%;height: auto;"')

  // 如果需要处理所有媒体元素
  if (type === 'all') {
    // 处理视频
    result = result.replace(
      /<video/gi,
      '<video class="richVideo" style="max-width: 100%;height: auto;"',
    )
  }

  return result
}

// 全球手机号匹配区号
/**
 * 根据手机号匹配对应的区号
 * 支持格式如 +86xxxxxxxxxxx、0086xxxxxxxxxxx、86xxxxxxxxxxx
 * 返回区号字符串，如 "+86"，未匹配返回空字符串
 */
export const matchPhoneCode = (phone: string): string => {
  if (!phone) return ''
  // 常见区号列表，可根据需要扩展
  const codeList = [
    '+86', // 中国大陆
    '+855', // 柬埔寨
    '+852', // 香港
    '+853', // 澳门
    '+886', // 台湾
    '+81', // 日本
    '+82', // 韩国
    '+1', // 美国/加拿大
    '+44', // 英国
    '+66', // 泰国
    '+84', // 越南
    '+62', // 印度尼西亚/印尼
    '+91', // 印度
    '+7', // 俄罗斯
    '+60', // 马来西亚
    '+63', // 菲律宾
    '+65', // 新加坡
    '+95', // 缅甸
    '+49', // 德国
    '+33', // 法国
  ]

  // 统一去除空格
  let p = phone.replace(/\s+/g, '')

  // 处理 00 开头的国际区号
  if (p.startsWith('00')) {
    p = '+' + p.slice(2)
  }

  // 处理不带+的区号（如86138xxxxxxx）
  if (!p.startsWith('+')) {
    // 尝试匹配前2-4位
    for (let len = 4; len >= 1; len--) {
      const code = '+' + p.slice(0, len)
      if (codeList.includes(code)) {
        return code
      }
    }
    return ''
  }

  // 处理带+的区号
  for (const code of codeList) {
    if (p.startsWith(code)) {
      return code
    }
  }

  return ''
}

// 客服
export const onLineService = () => {
  const userInfo = getStorage(storage.USER_INFO)
  window.location.href = `https://ibexmessage.com/index/index/home?visiter_id=&visiter_name=${userInfo?.nickName || ''}&avatar=&business_id=8&groupid=0&special=10`
}
